import { Button, useMediaQuery } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import GradientSuccessMarkIcon from "../../../assets/svgs/GradientSuccessMarkIcon";
import LoadingApp from "../../../components/LoadingApp";
import { BiometriaFacial } from "../../../interfaces/entities/BiometriaFacial";
import { BiometriaStatusEnum } from "../../../interfaces/enum/BiometriaStatusEnum";
import { ValidateCameraCaptureProps } from "../../../interfaces/props/SaudeOnline/IngressarNoAtendimento/ValidateCameraCaptureProps";
import { compararBiometriaFacial } from "../../../services/BiometriaFacial/BiometriaFacialService";
import ErrorIcon from "./ErrorIcon";
import { videoConstraints } from "./defaultStates";
import './styles.css';
import { sxButton, sxTryAgainButton } from "./sxProps";
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";

function ValidateCameraCapture(props: ValidateCameraCaptureProps) {
    const [loading, setLoading] = useState<boolean>(false);
    const [biometriaStatus, setBiometriaStatus] = useState<BiometriaStatusEnum>(BiometriaStatusEnum.Capturando);
    const [message, setMessage] = useState<string>('');
    const [back, setBack] = useState<boolean>(false);
    const [count, setCount] = useState<number>(5);
    const [countErrors, setCountErros] = useState<number>(0);
    const [startCounting, setStartCounting] = useState<boolean>(false)
    const webcamRef = useRef<Webcam>(null);
    const landscapeMobile = useMediaQuery('(max-width: 1130px) and (max-height: 600px)');

    const { fontSizeStyleAccessibility } = useAccessibility();


    useEffect(() => {
        if (startCounting) {
            setTimeout(() => {
                if (count > 0) {
                    setCount(count - 1);
                }
            }, 1000);
        }
        if (count === 0) {
            props.nextStep();
        }
    }, [count, startCounting]);

    const capture = useCallback(() => {

        var width = window.innerWidth;
        var height = window.innerHeight;
        var imageSrc:any;

        if (height > width) {
            imageSrc= webcamRef?.current?.getScreenshot({width: 354, height: 472});
        } else {
            imageSrc= webcamRef?.current?.getScreenshot();
        }     

        if (imageSrc) {
            setLoading(true);

            compararBiometriaFacial(imageSrc)?.then((response: BiometriaFacial | null) => {
                try {
                    const data = { ...response, result: JSON.parse(response?.result) };
                    setStartCounting(true)
                    setBiometriaStatus(BiometriaStatusEnum.Sucesso);
                    setLoading(false);
                } catch (error) {
                    setBiometriaStatus(BiometriaStatusEnum.Erro);
                    if (countErrors < 2) {
                        setMessage(response?.result)
                    }
                    else {
                        setMessage('Houve uma falha com o sistema de autenticação de biometria facial, você poderá cadastrar sua biometria mais tarde.')
                        setBack(true);
                    }
                    setLoading(false);
                }
            })
        }
    }, [webcamRef, countErrors]);

    const tryAgain = () => {
        if (countErrors < 2) {
            setCountErros(countErrors + 1)
        }
        else {
            setCountErros(0);
        }
        setBiometriaStatus(BiometriaStatusEnum.Capturando);
        setStartCounting(false);
        setCount(5);
    }

    const CaptureMessage = () => (
        <p className="camera-instructions" style={fontSizeStyleAccessibility} tabIndex={0}>
            Posicione seu rosto dentro da linha pontilhada, olhe para a câmera e clique no botão abaixo.
        </p>
    )

    const restartRegistartion = () => {
        setCountErros(0)
        setBiometriaStatus(BiometriaStatusEnum.Capturando);
        setStartCounting(false);
        setBack(false)
        setCount(5);
    }

    const [devices, setDevices] = useState<CameraDeviceProps[]>([]);
    
    const handleDevices = useCallback(
      (mediaDevices: MediaDeviceInfo[]) =>
        setDevices(mediaDevices.filter(({ kind }) => kind === "videoinput")),
      []
    );
    
    useEffect(() => {
      navigator.mediaDevices.enumerateDevices().then(handleDevices);
    }, [handleDevices]);

    return (
        <div className='camera-holder'>
            {loading ? <LoadingApp text={'Analisando imagem, aguarde...'} showLoading={loading} /> :
                <>
                    <div className="camera-capture">
                        {biometriaStatus === BiometriaStatusEnum.Erro && !loading &&
                            <div className="camera-result-holder">
                                <div className="icon-holder">
                                    <ErrorIcon />
                                </div>
                                <p className="camera-instructions">
                                    Falha de comunicação
                                </p>
                                <p className="countdown">
                                    {message}
                                </p>
                            </div>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Sucesso &&
                            <div className="camera-result-holder">
                                <div className="icon-holder">
                                    <GradientSuccessMarkIcon />
                                </div>
                                <p className="camera-instructions">
                                    Biometria validada com sucesso!
                                </p>
                                <p className="countdown">
                                    (Essa tela será fechada em {count}s)
                                </p>
                            </div>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Capturando && <div className="face-orientation" />}
                        {devices.length > 0 && 
                        <Webcam
                            ref={webcamRef}
                            mirrored={true}
                            audio={false}
                            height={'100%'}
                            width={'845px'}
                            className="camera"
                            screenshotFormat="image/jpeg"
                            videoConstraints={{
                                width: 1280,
                                height: 720,
                                facingMode: "user",
                                deviceId: devices[0].deviceId
                            }}
                        />}
                        {biometriaStatus === BiometriaStatusEnum.Capturando && !landscapeMobile &&
                            <CaptureMessage/>
                        }
                    </div>
                    <div className="camera-button">
                        {landscapeMobile && <CaptureMessage />}
                        {biometriaStatus === BiometriaStatusEnum.Capturando &&
                            <Button
                                variant="contained"
                                size="large"
                                onClick={capture}
                                sx={sxButton}
                                fullWidth>
                                CAPTURAR FOTO DE SEGURANÇA
                            </Button>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Erro && !back &&
                            <Button
                                variant="contained"
                                size="large"
                                onClick={tryAgain}
                                sx={sxButton}
                                fullWidth>
                                Tentar novamente
                            </Button>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Erro && back &&
                            <Button
                                variant="contained"
                                size="large"
                                onClick={props.nextStep}
                                sx={sxButton}
                                fullWidth>
                                Iniciar atendimento
                            </Button>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Erro && back &&
                            <div className="try-again-button">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={restartRegistartion}
                                    sx={sxTryAgainButton}
                                    fullWidth>
                                    Tentar novamente
                                </Button>
                            </div>
                        }
                    </div>
                </>
            }

        </div>
    );
}

export default ValidateCameraCapture;